import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import API_URL from '../config';
import SharedNav from './SharedNav';
import './PriorCandidates.css';

const STATUS_OPTIONS = ['Awaiting Review', 'Contacting', 'Reviewed', 'Rejected'];

function statusClass(s) {
  return 'pc-status-' + (s || '').replace(/\s+/g, '-');
}

// One instance of this drives either the Candidate Location or Job Location filter block --
// state -> target city (search radius from) -> multi-select checklist of nearby cities with
// real distances, mirroring the approved mockup exactly.
function LocationBlock({ title, kind, states, authHeaders, onCitiesChange }) {
  const [state, setState] = useState('');
  const [cities, setCities] = useState([]); // cities available in this state
  const [target, setTarget] = useState('');
  const [nearby, setNearby] = useState([]);
  const [selectedCities, setSelectedCities] = useState(new Set());
  const [panelOpen, setPanelOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setPanelOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    onCitiesChange([...selectedCities]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCities]);

  const handleStateChange = async (e) => {
    const s = e.target.value;
    setState(s);
    setTarget('');
    setNearby([]);
    setSelectedCities(new Set());
    setPanelOpen(false);
    if (!s) { setCities([]); return; }
    try {
      const { data } = await axios.get(`${API_URL}/api/prior-candidates/cities`, { ...authHeaders(), params: { state: s, kind } });
      setCities(data);
    } catch { setCities([]); }
  };

  const handleTargetChange = async (e) => {
    const city = e.target.value;
    setTarget(city);
    if (!city) { setNearby([]); return; }
    setSelectedCities(prev => new Set(prev).add(`${city}|${state}`));
    try {
      const { data } = await axios.get(`${API_URL}/api/prior-candidates/cities/nearby`, { ...authHeaders(), params: { city, state } });
      setNearby(data);
      setPanelOpen(true);
    } catch { setNearby([]); }
  };

  const toggleCity = (key) => {
    setSelectedCities(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const removeChip = (key) => {
    setSelectedCities(prev => { const next = new Set(prev); next.delete(key); return next; });
  };

  return (
    <div className="pc-loc-block" ref={rootRef}>
      <div className="pc-loc-block-title">{title}</div>
      <div className="pc-loc-row">
        <div className="pc-filter-group">
          <label>State</label>
          <select value={state} onChange={handleStateChange}>
            <option value="">All States</option>
            {states.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="pc-filter-group">
          <label>Target City (search radius from)</label>
          <select value={target} onChange={handleTargetChange} disabled={!state}>
            <option value="">{state ? 'Choose a target city…' : 'Select a state first'}</option>
            {cities.map(c => <option key={c.city} value={c.city}>{c.city}, {state}</option>)}
          </select>
        </div>
        <div className="pc-filter-group" style={{ position: 'relative' }}>
          <label>&nbsp;</label>
          <button
            type="button"
            className={`pc-radius-btn${selectedCities.size > 0 ? ' pc-active' : ''}`}
            disabled={!target}
            onClick={() => setPanelOpen(o => !o)}
          >
            Choose cities within 50 mi ▾
          </button>
          {panelOpen && (
            <div className="pc-radius-panel">
              {nearby.length === 0 ? (
                <div className="pc-empty">No nearby cities found.</div>
              ) : nearby.map(c => {
                const key = `${c.city}|${c.state}`;
                return (
                  <label className="pc-radius-item" key={key}>
                    <input type="checkbox" checked={selectedCities.has(key)} onChange={() => toggleCity(key)} />
                    <span className="pc-rc-name">{c.city}, {c.state}</span>
                    <span className="pc-rc-dist">{c.dist < 0.5 ? 'target' : `${Math.round(c.dist)} mi`}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <div className="pc-city-chips">
        {[...selectedCities].map(key => {
          const [city, st] = key.split('|');
          return (
            <span className="pc-city-chip" key={key}>
              {city}, {st}
              <button type="button" onClick={() => removeChip(key)}>✕</button>
            </span>
          );
        })}
      </div>
    </div>
  );
}

export default function PriorCandidates({ user, onLogout }) {
  const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const [companies, setCompanies] = useState([]);
  const [targetCompany, setTargetCompany] = useState('');
  const [states, setStates] = useState({ candidateStates: [], jobStates: [], originalCustomers: [] });

  const [originalCustomer, setOriginalCustomer] = useState('');
  const [status, setStatus] = useState('');
  const [candidateCities, setCandidateCities] = useState([]);
  const [jobCities, setJobCities] = useState([]);
  const [cdlFilter, setCdlFilter] = useState('');
  const [deliveryFilter, setDeliveryFilter] = useState('');
  const [lastContactedDays, setLastContactedDays] = useState('');

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [modalOpen, setModalOpen] = useState(false);
  const [template, setTemplate] = useState('Hi {{name}}, this is Mountain Recruiting reaching out about the FedEx Ground driver role you applied for. Are you still interested?');
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState('');

  const limit = 100;

  useEffect(() => {
    axios.get(`${API_URL}/api/prior-candidates/companies`, authHeaders())
      .then(({ data }) => setCompanies(data)).catch(() => {});
    axios.get(`${API_URL}/api/prior-candidates/states`, authHeaders())
      .then(({ data }) => setStates(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (targetCompany) params.targetCompany = targetCompany;
      if (originalCustomer) params.originalCustomer = originalCustomer;
      if (status) params.status = status;
      if (candidateCities.length) params.candidateCities = candidateCities.join('|');
      if (jobCities.length) params.jobCities = jobCities.join('|');
      if (cdlFilter) params.cdlFilter = cdlFilter;
      if (deliveryFilter) params.deliveryFilter = deliveryFilter;
      if (lastContactedDays && targetCompany) params.lastContactedDays = lastContactedDays;
      const { data } = await axios.get(`${API_URL}/api/prior-candidates`, { ...authHeaders(), params });
      setRows(data.rows);
      setTotal(data.total);
    } catch {
      setRows([]); setTotal(0);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, targetCompany, originalCustomer, status, candidateCities, jobCities, cdlFilter, deliveryFilter, lastContactedDays]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSelected(new Set()); }, [targetCompany, originalCustomer, status, candidateCities, jobCities, cdlFilter, deliveryFilter, lastContactedDays, page]);
  // Last Contacted only means anything once a company is selected -- clear it if the company
  // selection is cleared, rather than leaving a filter silently applied with nothing to scope it to.
  useEffect(() => { if (!targetCompany) setLastContactedDays(''); }, [targetCompany]);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3500); };

  const toggleOne = (id) => {
    if (!targetCompany) { showToast('Select a Customer Recruiting For first.'); return; }
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else {
        if (next.size >= 100) { showToast('Max 100 candidates per send.'); return prev; }
        next.add(id);
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (!targetCompany) { showToast('Select a Customer Recruiting For first.'); return; }
    const allSelected = rows.length > 0 && rows.every(r => selected.has(r.id));
    if (allSelected) {
      setSelected(new Set());
    } else {
      const next = new Set();
      rows.forEach(r => { if (next.size < 100) next.add(r.id); });
      if (rows.length > 100) showToast('Only the first 100 visible rows were selected (max per send).');
      setSelected(next);
    }
  };

  const clearFilters = () => {
    setOriginalCustomer(''); setStatus('');
    setCandidateCities([]); setJobCities([]);
    setCdlFilter(''); setDeliveryFilter(''); setLastContactedDays('');
    setPage(1);
  };

  const openModal = () => setModalOpen(true);
  const closeModal = () => setModalOpen(false);

  const sampleName = () => {
    const idx = [...selected][0];
    const row = rows.find(r => r.id === idx);
    return row ? row.name.split(' ')[0] : 'there';
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/prior-candidates/send-text`, {
        poolIds: [...selected], companyId: targetCompany, message: template,
      }, authHeaders());
      closeModal();
      setSelected(new Set());
      showToast(`Sent to ${data.created} candidate${data.created === 1 ? '' : 's'} for ${data.companyName}. ${data.alreadyContacted ? `${data.alreadyContacted} were already Prior for this company and skipped.` : ''} Texts will go out over the next minute or two, paced to stay under RingCentral's rate limit.`);
      load();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to send.');
    } finally {
      setSending(false);
    }
  };

  const targetCompanyName = companies.find(c => String(c.id) === String(targetCompany))?.name || '';
  const sendDisabled = selected.size === 0 || !targetCompany;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="pc-page">
      <SharedNav user={user} onLogout={onLogout} />
      <div className="pc-wrap">
        <div className={`pc-target-bar${!targetCompany ? ' pc-unset' : ''}`}>
          <label>Customer Recruiting For</label>
          <select value={targetCompany} onChange={e => { setTargetCompany(e.target.value); setPage(1); }}>
            <option value="">Select a customer…</option>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        <div className="pc-filters">
          <div className="pc-filter-group">
            <label>Original Applied-To Customer</label>
            <select value={originalCustomer} onChange={e => { setOriginalCustomer(e.target.value); setPage(1); }}>
              <option value="">All</option>
              {states.originalCustomers.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className="pc-filter-group">
            <label>Status</label>
            <select value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}>
              <option value="">All Statuses</option>
              {STATUS_OPTIONS.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="pc-filter-group">
            <label>CDL</label>
            <select value={cdlFilter} onChange={e => { setCdlFilter(e.target.value); setPage(1); }}>
              <option value="">All</option>
              <option value="any">Any CDL (incl. Team)</option>
              <option value="cdl_team">CDL-Team only</option>
              <option value="no_cdl">No CDL</option>
            </select>
          </div>
          <div className="pc-filter-group">
            <label>Delivery Experience</label>
            <select value={deliveryFilter} onChange={e => { setDeliveryFilter(e.target.value); setPage(1); }}>
              <option value="">All</option>
              <option value="yes">Has Delivery Experience</option>
            </select>
          </div>
          <div className="pc-filter-group">
            <label>Last Contacted {!targetCompany && <span className="pc-filter-hint">(select a customer first)</span>}</label>
            <select value={lastContactedDays} disabled={!targetCompany} onChange={e => { setLastContactedDays(e.target.value); setPage(1); }}>
              <option value="">Any time</option>
              <option value="30">Not contacted in last 30 days</option>
              <option value="60">Not contacted in last 60 days</option>
              <option value="90">Not contacted in last 90 days</option>
              <option value="120">Not contacted in last 120 days</option>
            </select>
          </div>
          <button className="pc-clear-btn" onClick={clearFilters}>Clear all filters</button>
        </div>

        <div className="pc-loc-filters">
          <LocationBlock title="Candidate Location" kind="candidate" states={states.candidateStates} authHeaders={authHeaders} onCitiesChange={(c) => { setCandidateCities(c.map(k => k.split('|')[0])); setPage(1); }} />
          <LocationBlock title="Job Location" kind="job" states={states.jobStates} authHeaders={authHeaders} onCitiesChange={(c) => { setJobCities(c.map(k => k.split('|')[0])); setPage(1); }} />
        </div>

        <div className="pc-toolbar">
          <div className="pc-toolbar-left">
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input type="checkbox" disabled={!targetCompany} checked={rows.length > 0 && rows.every(r => selected.has(r.id))} onChange={toggleAll} />
              Select all visible
            </label>
            <span><span className={`pc-sel-count${selected.size >= 100 ? ' pc-warn' : ''}`}>{selected.size}</span> / 100 selected</span>
            <span>{total.toLocaleString()} total candidates</span>
          </div>
          <div style={{ textAlign: 'right' }}>
            <button className="pc-send-btn" disabled={sendDisabled} onClick={openModal}>Send Automated Text</button>
            <div className="pc-send-hint">Texts Q.Phone if present, otherwise the main Phone number</div>
          </div>
        </div>

        <div className="pc-table-shell">
          <div className="pc-table-scroll">
            <table className="pc-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Name</th>
                  <th>Relationship</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Q.Email</th>
                  <th>Q.Phone</th>
                  <th>Status</th>
                  <th>CDL</th>
                  <th>Delivery Exp.</th>
                  <th>Candidate City</th>
                  <th>Candidate State</th>
                  <th>Job Title</th>
                  <th>Job City</th>
                  <th>Job State</th>
                  <th>Date</th>
                  <th>Interest Level</th>
                  <th>Source</th>
                  <th>Original Customer</th>
                  <th>Relevant Experience</th>
                  <th>Education</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={21} style={{ textAlign: 'center', padding: 24 }}>Loading…</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={21} style={{ textAlign: 'center', padding: 24 }}>No candidates match these filters.</td></tr>
                ) : rows.map(c => {
                  const unlocked = c.relationship === 'prior';
                  return (
                    <tr key={c.id} className={selected.has(c.id) ? 'pc-selected' : ''}>
                      <td><input type="checkbox" disabled={!targetCompany} checked={selected.has(c.id)} onChange={() => toggleOne(c.id)} /></td>
                      <td className="pc-name-cell">{c.name}</td>
                      <td>
                        {c.relationship === 'no_company_selected' && <span className="pc-not-contacted">Select a customer</span>}
                        {c.relationship === 'not_contacted' && <span className="pc-not-contacted">Not yet contacted</span>}
                        {unlocked && <span className="pc-prior-badge">Prior — {targetCompanyName}</span>}
                      </td>
                      <td>{c.email}</td>
                      <td>{c.phoneAvailable ? (unlocked ? c.phone : <span className="pc-locked">🔒 Locked</span>) : <span className="pc-q-empty">—</span>}</td>
                      <td className={c.qEmail ? 'pc-q-cell' : 'pc-q-empty'}>{c.qEmail || '—'}</td>
                      <td>{c.qPhoneAvailable ? (unlocked ? c.qPhone : <span className="pc-locked">🔒 Locked</span>) : <span className="pc-q-empty">—</span>}</td>
                      <td><span className={`pc-status-pill ${statusClass(c.status)}`}>{c.status}</span></td>
                      <td>{c.cdl ? <span className={`pc-cdl-badge${c.cdl === 'CDL-Team' ? ' pc-cdl-team-badge' : ''}`}>{c.cdl}</span> : <span className="pc-q-empty">—</span>}</td>
                      <td>{c.deliveryExperience ? <span className="pc-delivery-badge">Yes</span> : <span className="pc-q-empty">—</span>}</td>
                      <td>{c.candidateCity}</td>
                      <td>{c.candidateState}</td>
                      <td>{c.jobTitle}</td>
                      <td>{c.jobCity}</td>
                      <td>{c.jobState}</td>
                      <td>{c.date}</td>
                      <td>{c.interestLevel || '—'}</td>
                      <td>{c.source}</td>
                      <td><span className="pc-customer-tag">{c.originalCustomer}</span></td>
                      <td>{c.relevantExperience || '—'}</td>
                      <td>{c.education || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="pc-pagination">
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
            <span>Page {page} of {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
          </div>
        </div>
      </div>

      {modalOpen && (
        <div className="pc-modal-overlay">
          <div className="pc-modal">
            <div className="pc-modal-head">
              <h2>Send Automated Text</h2>
              <p>{selected.size} candidates selected for {targetCompanyName}</p>
            </div>
            <div className="pc-modal-body">
              <textarea value={template} onChange={e => setTemplate(e.target.value)} />
              <div className="pc-modal-preview">Preview → {template.replace(/\{\{name\}\}/g, sampleName())}</div>
            </div>
            <div className="pc-modal-foot">
              <button className="pc-btn-secondary" onClick={closeModal} disabled={sending}>Cancel</button>
              <button className="pc-send-btn" onClick={handleSend} disabled={sending}>
                {sending ? 'Sending…' : `Send to ${selected.size}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <div className="pc-toast">{toast}</div>}
    </div>
  );
}
